import { PRODUCT_CONFIG } from "@stb/product-config"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { organizationJsonLd } from "@/core/seo/json-ld"

import { capKm } from "../../components/home/home-content"
import type { HomeCopy } from "../../components/home/home-copy"
import { DATA_COUNTRY_ZH, formatDateZh } from "./dates"

/** Chinese counterpart of the home page copy (home-content.ts); terms: docs/design/landing/copy-zh.md. */
export const HOME_SEO_ZH = {
  title: "里程、收据和发票应用",
  description: `用 GPS 记录工作行程、扫描收据、开出 Tax Invoice，专为澳大利亚个体经营者设计。数据存储在${DATA_COUNTRY_ZH}，可试用 ${TRIAL_DAYS} 天。`,
  // Organization only: no Offer until prices are final, no FAQPage (seo-spec).
  jsonLd: [organizationJsonLd()],
}

export const HOME_COPY_ZH: HomeCopy = {
  trialBadge: `${TRIAL_DAYS} 天免费试用，无需绑定银行卡。`,
  h1: "为澳洲个体经营者准备的里程、收据和发票应用",
  lead: "用手机记录工作行程，拍下收据，发出 Tax Invoice。所有记录放在一处，报税季和会计师都用得上。",
  startTrial: "开始免费试用",
  seeHow: "看看怎么用",
  appsGroupLabel: "手机应用即将上线",
  comingSoon: "即将上线",
  statLabels: {
    rate: (year) => `${year} 财年费率`,
    cap: "每辆车上限",
    records: "ATO 记录保存规定",
  },
  regionStat: { figure: DATA_COUNTRY_ZH, label: "数据存放地" },
  recordsFigure: "5 年",
  jobsTitle: "一个应用，管住占用你晚上时间的三件事",
  jobs: [
    {
      title: "里程日志",
      body: "手机 GPS 自动记录行程。比较 cents per km 法与 logbook 法，再把行程日志下载成 CSV。",
    },
    {
      title: "收据和开支",
      body: "拍下收据或上传 PDF。我们读出商家、日期、总额和 GST，由你核对后才算数。",
    },
    {
      title: "Tax Invoice 与 GST",
      body: "用邮件发送带 PDF 的 Tax Invoice，每张自动算好 GST，并看谁已付款。",
    },
  ],
  howTitle: "使用方法",
  steps: [
    {
      title: "安装并登录",
      body: "用邮箱登录，不用记密码。添加你的车，有 ABN 的话也一并填上。",
    },
    {
      title: "开车、拍照、开发票",
      body: "行程在后台自动记录。一路拍下收据。在网页或手机上开发票。",
    },
    {
      title: "报税时导出",
      body: "把行程日志下载成 CSV，发票下载成 PDF，或把全部记录导出成 zip，交给会计师。",
    },
  ],
  howMore: "Kestly 的使用方法，一步一步看",
  claimTitle: "申报车辆开支有两种方法，我们两种都帮你备好",
  claimSources: {
    individuals: "ATO，cents per km 法（个人）",
    business: "ATO，cents per km 法（企业）",
  },
  claimAnswer: ({ capKm: cap, year, cents }) =>
    `个体经营者可以用 cents per km 法（每辆车每年最多 ${cap} 个业务公里）或 logbook 法（按业务用途占比分摊实际开支）申报工作用车。${year} 财年的 cents per km 费率是 ${cents} 分。`,
  lastReviewed: (date) => `最近复核 ${date}`,
  methodsTable: {
    caption: "申报车辆开支的两种方法",
    columns: ["", "cents per km 法", "Logbook 法"],
    rows: [
      { header: "适合", cells: ["公里数少，申报简单", "公里数多，用车成本高"] },
      { header: "你需要保留", cells: ["公里数是怎么算出来的", "12 周行车日志和收据"] },
      { header: "上限", cells: [`每辆车 ${capKm} km`, "无"] },
    ],
  },
  recordsTitle: "你的记录，妥善保管",
  recordsStored: `存放在${DATA_COUNTRY_ZH}`,
  recordsStoredBody: ["账户数据和收据文件存放在澳大利亚。完整清单见", "。"],
  trustLinkText: "安全与隐私页面",
  fiveYearTitle: "ATO 的 5 年规定",
  fiveYearBody:
    "ATO 要求大多数经营记录保存 5 年。你可以随时导出。付费套餐失效后，我们最多再保留你的数据 3 个月，并在数据从账户中移除之前多次提醒你导出。",
  atoRecordsLabel: "ATO，保存经营记录",
  exportTitle: "随时带走",
  exportBody: "行程、收据和发票随时可以导出成 CSV 或 PDF；付费套餐失效后的 3 个月内同样可以。",
  pricingTitle: "简单明了的价格",
  perMonth: " / 月",
  draftBadge: "草案价格",
  plans: [
    {
      name: "免费版",
      note: `每月 ${PRODUCT_CONFIG.plans.free.tripsMonthly} 个 GPS 行程和 ${PRODUCT_CONFIG.plans.free.invoicesMonthly} 张发票`,
    },
    { name: "Logbook", note: "不限 GPS 行程，含 logbook 法" },
    { name: "Pro", note: "含 GST 的发票，以及收据" },
  ],
  draftNote: "草案，非最终价格。",
  pricingMore: "查看全部套餐、年付价格和团队套餐",
  toolsTitle: "免费工具和指南",
  freeToolsHeading: "免费工具",
  guidesHeading: "指南",
  compareTitle: "把 Kestly 和其他选择比一比",
  faqTitle: "常见问题",
  faq: [
    {
      question: "它能代替我的会计师吗？",
      answer:
        "不能。它帮你把记录整理好，并导出成会计师能用的格式。扣除额和 GST 金额都是估算，不是税务建议。",
    },
    {
      question: "我需要 ABN 吗？",
      answer: "里程日志不需要。发票和开支需要 ABN。",
    },
    {
      question: "没有信号时应用怎么办？",
      answer: "行程和收据先存在手机上，联网后自动同步。",
    },
  ],
  faqMore: "更多问题和解答",
  bandTitle: `先免费试用 ${TRIAL_DAYS} 天`,
  bandDescription: "无需绑定银行卡。试用结束后如果不订阅，可以继续使用免费版。",
  formatCheckedOn: formatDateZh,
  displayYear: (fy) => fy.replace("-", "–"),
}
